import React, { useState, useEffect } from "react";
import { Outlet, Link, useLocation } from "react-router";
import { motion, AnimatePresence } from "motion/react";
import { Menu, X, Github, Linkedin, ExternalLink, Mail } from "lucide-react";
import { BackToTop } from "./back-to-top";
import { ThemeToggle } from "./theme-toggle";
import { WhatsAppButton } from "./whatsapp-button";

export function Layout() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: "Home", href: "/", isExternal: false },
    { name: "About", href: "/about", isExternal: false },
    { name: "Services", href: "/services", isExternal: false },
    { name: "Pricing", href: "/pricing", isExternal: false },
    { name: "Blog", href: "/blog", isExternal: false },
    { name: "Contact", href: "/contact", isExternal: false },
  ];

  const homeAnchors = [
    { name: "Experience", href: "#experience" },
    { name: "Projects", href: "#projects" },
    { name: "Skills", href: "#skills" },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-violet-500/30 selection:text-white transition-colors duration-300">
      {/* Dynamic Background Pattern */}
      <div className="fixed inset-0 z-0 pointer-events-none opacity-20">
        <div className="absolute inset-0 bg-[radial-gradient(var(--color-border)_1px,transparent_1px)] [background-size:40px_40px]" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background" />
      </div>

      <nav className="fixed top-0 w-full z-50 bg-background/60 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="text-2xl font-black tracking-tighter flex items-center gap-1 group">
            <span className="bg-gradient-to-r from-violet-500 to-fuchsia-500 bg-clip-text text-transparent group-hover:from-fuchsia-500 group-hover:to-violet-500 transition-all duration-500">ERGYS</span>
            <span className="text-foreground">ONUZI</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-10">
            <div className="flex gap-10">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`text-sm font-black uppercase tracking-widest hover:text-foreground transition-all ${location.pathname === link.href ? 'text-violet-400' : 'text-muted-foreground'}`}
                >
                  {link.name}
                </Link>
              ))}
              
              {location.pathname === "/" && homeAnchors.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-sm font-black uppercase tracking-widest text-muted-foreground hover:text-foreground transition-all"
                >
                  {link.name}
                </a>
              ))}
            </div>
            <ThemeToggle />
          </div>

          {/* Mobile Toggle */}
          <div className="flex items-center gap-4 md:hidden">
            <ThemeToggle />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="w-12 h-12 flex items-center justify-center bg-secondary border border-border rounded-2xl text-foreground hover:border-violet-500 transition-all"
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-40 bg-background/95 backdrop-blur-2xl md:hidden flex flex-col items-center justify-center"
          >
            <div className="flex flex-col items-center gap-8 text-center">
              {navLinks.map((link, idx) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Link
                    to={link.href}
                    className={`text-4xl font-black uppercase tracking-tighter italic ${location.pathname === link.href ? 'text-violet-500' : 'text-foreground/50'}`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
            </div>
            
            <button 
              onClick={() => setIsMenuOpen(false)}
              className="absolute top-8 right-8 w-16 h-16 rounded-full border border-border flex items-center justify-center text-foreground"
            >
              <X size={32} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <main>
        <Outlet />
      </main>

      <footer className="border-t border-border py-24 bg-background relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[1px] bg-gradient-to-r from-transparent via-violet-500/20 to-transparent" />
        
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-16 mb-16">
            <div className="col-span-2">
              <Link to="/" className="text-3xl font-black tracking-tighter flex items-center gap-1 mb-6">
                <span className="bg-gradient-to-r from-violet-500 to-fuchsia-500 bg-clip-text text-transparent">ERGYS</span>
                <span className="text-foreground">ONUZI</span>
              </Link>
              <p className="text-muted-foreground text-lg max-w-sm leading-relaxed font-medium">
                Designing and developing high-performance web applications that drive business growth and user engagement.
              </p>
            </div>
            
            <div>
              <h4 className="text-xs font-black uppercase tracking-[0.4em] text-foreground mb-8">Navigation</h4>
              <div className="flex flex-col gap-4">
                {navLinks.map(link => (
                  <Link key={link.name} to={link.href} className="text-muted-foreground hover:text-foreground transition-colors font-bold uppercase tracking-widest text-[10px]">{link.name}</Link>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-xs font-black uppercase tracking-[0.4em] text-foreground mb-8">Connect</h4>
              <div className="flex flex-col gap-4">
                <a 
                  href="https://github.com/ErgysOnuzi" 
                  target="_blank" 
                  rel="noreferrer" 
                  className="text-muted-foreground hover:text-foreground transition-colors font-bold uppercase tracking-widest text-[10px] flex items-center gap-2"
                  aria-label="Visit Ergys Onuzi on GitHub"
                >
                  <Github size={14} aria-hidden="true" /> GitHub
                </a>
                <a 
                  href="https://al.linkedin.com/in/ergys-onuzi-3131aa193" 
                  target="_blank" 
                  rel="noreferrer" 
                  className="text-muted-foreground hover:text-foreground transition-colors font-bold uppercase tracking-widest text-[10px] flex items-center gap-2"
                  aria-label="Visit Ergys Onuzi on LinkedIn"
                >
                  <Linkedin size={14} aria-hidden="true" /> LinkedIn
                </a>
                <a 
                  href="mailto:ergysonuzi12@gmail.com" 
                  className="text-muted-foreground hover:text-foreground transition-colors font-bold uppercase tracking-widest text-[10px] flex items-center gap-2"
                  aria-label="Email Ergys Onuzi"
                >
                  <Mail size={14} aria-hidden="true" /> Email
                </a>
              </div>
            </div>
          </div>

          <div className="pt-16 border-t border-border flex flex-col md:flex-row justify-between items-center gap-8">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-muted-foreground">
              © {new Date().getFullYear()} Ergys Onuzi. Crafted with precision.
            </p>
            <div className="flex items-center gap-8">
              <Link to="/admin" className="text-[10px] font-black uppercase tracking-[0.3em] text-muted-foreground hover:text-foreground transition-colors">System Admin</Link>
            </div>
          </div>
        </div>
      </footer>
      <BackToTop />
      <WhatsAppButton />
    </div>
  );
}